import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TreeNode } from 'primereact/treenode';
import { TreeSelectProps } from 'primereact/treeselect';

import ButtonCustom from '@/components/common/ui/button';
import { CalendarCustomForm } from '@/components/common/ui/calendar';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { TreeSelectCustomForm } from '@/components/common/ui/treeSelect';
import { useTranslation } from '@/config/i18n';
import useSearch from '@/hooks/useSearch';
import { DATE_FORMAT } from '@/shared/constants';
import { STAGE_GRADUATION_STATUS } from '@/shared/constants/user';
import { UserFormSearchType, userSearchFormSchema } from '@/shared/schema/user';
import { parseQueryValue } from '@/lib/utils';

type Props = {
  onSearch: () => void;
};

const CoachRosterSearch: React.FC<Props> = ({ onSearch }) => {
  const [t] = useTranslation('');
  const { handleSearch, queryRaw } = useSearch();

  const formMethods = useForm<UserFormSearchType>({
    mode: 'onChange',
    resolver: yupResolver(userSearchFormSchema),
    shouldFocusError: false,
    defaultValues: {
      keyword: '',
      status: 'all',
      start_date: null,
      end_date: null,
      stage_graduation_status: 'all',
    },
  });
  const { handleSubmit, getValues, setValue, reset, watch } = formMethods;
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const statusTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.status_label')}
          {selectedNodes[0].label}
        </>
      );
    }
    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );
    return (
      <>
        {t('user_list.status_label')}
        {defaultOption?.label}
      </>
    );
  };

  const progressTemplate = (
    selectedNodes: TreeNode | TreeNode[],
    props: TreeSelectProps,
  ) => {
    if (Array.isArray(selectedNodes) && selectedNodes.length > 0) {
      return (
        <>
          {t('user_list.progress_label')}
          {selectedNodes[0].label}
        </>
      );
    }
    const defaultOption = props.options?.find(
      (option) => option.key === props.value,
    );
    return (
      <>
        {t('user_list.progress_label')}
        {defaultOption?.label}
      </>
    );
  };

  const getAllFieldNames = () => Object.keys(getValues());

  // Restore the form from the URL so filters survive a reload.
  function mapValueFromQuery() {
    getAllFieldNames().forEach((field: any) => {
      const val = queryRaw[field];
      if (!val) return;
      setValue(field, parseQueryValue(field, val));
    });
  }

  useEffect(() => {
    mapValueFromQuery();
  }, []);

  function onSubmit() {
    const { keyword, status, stage_graduation_status, start_date, end_date } =
      getValues();

    handleSearch({
      keyword,
      status: status === 'all' ? '' : status,
      stage_graduation_status:
        stage_graduation_status === 'all' ? '' : stage_graduation_status,
      start_date: start_date
        ? dayjs(start_date.toString()).format('YYYY-MM-DD')
        : '',
      end_date: end_date ? dayjs(end_date.toString()).format('YYYY-MM-DD') : '',
    });
    // handleSearch is a no-op when nothing changed, so tell the list explicitly.
    onSearch();
  }

  function handleReset() {
    reset();
    handleSearch({ reset: true });
    onSearch();
  }

  return (
    <FormProvider {...formMethods}>
      <form className="mt-[16px]" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex">
          <div className="w-full max-w-[340px] search-input relative">
            <span className="pi pi-search absolute top-[14px] left-[14px] text-gray-500 text-[15px] z-40" />
            <InputTextCustomForm
              name="keyword"
              className="!pl-[40px]"
              placeholder={t('user_list.keyword_coach')}
            />
          </div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="start_date"
            dateFormat={DATE_FORMAT}
            wrapClass="ml-4 w-[160px]"
            showIcon
            maxDate={endDate ? new Date(endDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <div className="flex-full-center ml-4">~</div>
          <CalendarCustomForm
            className="date-custom"
            placeholder="----/--/--"
            name="end_date"
            dateFormat={DATE_FORMAT}
            wrapClass="ml-4 w-[160px]"
            showIcon
            minDate={startDate ? new Date(startDate ?? '') : undefined}
            icon={() => <i className="pi pi-calendar text-[18px]" />}
          />
          <TreeSelectCustomForm
            name="status"
            wrapClass="ml-4 w-[193px]"
            panelClassName="tree-hide-header tree-disabled-unselect"
            optionsStatic={[
              { key: 'all', label: t('common.status.all') },
              { key: 'active', label: t('common.status.active') },
              { key: 'inactive', label: t('common.status.inactive') },
            ]}
            valueTemplate={statusTemplate}
          />
          <TreeSelectCustomForm
            name="stage_graduation_status"
            wrapClass="ml-4 w-[193px]"
            panelClassName="tree-hide-header tree-disabled-unselect"
            optionsStatic={[
              { key: 'all', label: t('common.status.all') },
              {
                key: STAGE_GRADUATION_STATUS.FREE,
                label: t('user_list.graduation_free_for_coach'),
              },
              {
                key: STAGE_GRADUATION_STATUS.PAID,
                label: t('user_list.graduation_billing'),
              },
              {
                key: STAGE_GRADUATION_STATUS.TRIAL,
                label: t('user_list.graduation_free_for_student'),
              },
            ]}
            valueTemplate={progressTemplate}
          />
          <div className="action flex ml-4">
            <ButtonCustom
              type="button"
              severity="secondary"
              onClick={() => handleReset()}
            >
              <span className="pi pi-refresh" />
            </ButtonCustom>
            <ButtonCustom type="submit" className="ml-2">
              <span className="pi pi-search" />
            </ButtonCustom>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default CoachRosterSearch;
